import { APP_NAME } from '@circa/shared/config';
import { decryptJson, encryptJson } from '@circa/shared/crypto';
import { useSyncExternalStore } from 'react';
import { deleteMeta, getMeta, setMeta } from '@/core/db';
import { keys } from '@/core/keys';
import { isAndroid } from '@/core/platform';
import { getSetting, setSetting, useSetting } from '@/core/settings';
import { dayInZone } from '@/core/time';

// Trace / The World App: movement (walks, drives, rides, trains…), flights, stays and places
// for the journal. Orbit pairs as a device of the World App hub (Trace syncs into the same
// hub) and keeps its own copy of the rows it needs, encrypted with the data key.

export const DEFAULT_HUB = 'https://theworldapp.theworld-mp.workers.dev';
const CACHE_KEY = 'world.cache';
const SETTING = 'integrations.world';

export interface WorldConn {
  origin: string;
  token: string;
  deviceId: string;
  connectedAt: number;
}

/** One trip Trace recorded. */
export interface Movement {
  id: string;
  /** walk, run, hike, ride, drive, passenger, rideshare, taxi, bus, train, boat, other */
  category: string;
  /** epoch ms */
  start: number;
  end: number;
  distanceM?: number;
  fromName?: string;
  toName?: string;
  name?: string;
  /** IANA zone the trip happened in */
  tz?: string;
  source?: string;
}

export interface Place {
  id: string;
  name: string;
  category?: string;
  country?: string;
  date?: string;
  kind?: string;
  firstVisit?: string;
  wantToGo?: boolean;
}

export interface Flight {
  id: string;
  date: string;
  from: string;
  to: string;
  fromCity?: string;
  toCity?: string;
  flightNo?: string;
  km?: number;
  source?: string;
}

export interface Stay {
  id: string;
  hotel: string;
  city?: string;
  checkIn: string;
  checkOut: string;
}

export interface Sighting {
  id: string;
  day: string;
  city?: string;
  country?: string;
}

export interface WorldCache {
  v: 1;
  origin: string;
  next: number;
  syncedAt: number;
  tracks: Record<string, Movement>;
  flights: Record<string, Flight>;
  stays: Record<string, Stay>;
  places: Record<string, Place>;
  sightings: Record<string, Sighting>;
}

// ------------------------------------------------------------------ connection

export function getWorldConn(): WorldConn | null {
  return getSetting<WorldConn | null>(SETTING, null);
}

export function useWorldConn(): WorldConn | null {
  return useSetting<WorldConn | null>(SETTING, null)[0];
}

async function readError(res: Response): Promise<string> {
  try {
    const j = (await res.json()) as { error?: string; message?: string };
    return j.error ?? j.message ?? res.statusText;
  } catch {
    return res.statusText || `HTTP ${res.status}`;
  }
}

/** Claim a pairing code from Trace (Settings → Add a device) or The World App (Sync → Add a device). */
export async function pairWorld(code: string, origin = DEFAULT_HUB): Promise<WorldConn> {
  const base = origin.trim().replace(/\/+$/, '');
  let res: Response;
  try {
    res = await fetch(`${base}/pair/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code: code.trim().toUpperCase(),
        name: APP_NAME,
        platform: isAndroid ? 'android' : 'web',
      }),
    });
  } catch {
    throw new Error("Can't reach The World App's server. Check the connection and try again.");
  }
  if (!res.ok)
    throw new Error(
      res.status === 403
        ? "That code didn't work. Make a new one and try again."
        : await readError(res),
    );
  const j = (await res.json()) as { token: string; deviceId: string };
  const conn: WorldConn = {
    origin: base,
    token: j.token,
    deviceId: j.deviceId,
    connectedAt: Date.now(),
  };
  setSetting(SETTING, conn);
  cache = null;
  await deleteMeta(CACHE_KEY);
  emit();
  return conn;
}

export async function disconnectWorld() {
  setSetting(SETTING, null);
  cache = null;
  await deleteMeta(CACHE_KEY);
  emit();
}

// ------------------------------------------------------------------ cache

let cache: WorldCache | null = null;
let version = 0;
const listeners = new Set<() => void>();
function emit() {
  version++;
  for (const l of listeners) l();
}

/** Re-renders when the movement cache changes (after a pull). */
export function useWorldVersion(): number {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => version,
  );
}

const emptyCache = (origin: string): WorldCache => ({
  v: 1,
  origin,
  next: 0,
  syncedAt: 0,
  tracks: {},
  flights: {},
  stays: {},
  places: {},
  sightings: {},
});

export async function loadWorldCache(): Promise<WorldCache | null> {
  if (cache) return cache;
  const bytes = await getMeta<Uint8Array>(CACHE_KEY);
  if (!bytes) return null;
  try {
    cache = await decryptJson<WorldCache>(keys.dataKey(), bytes);
    emit();
  } catch {
    cache = null;
  }
  return cache;
}

export const peekWorldCache = () => cache;

async function saveCache(c: WorldCache) {
  await setMeta(CACHE_KEY, await encryptJson(keys.dataKey(), c));
}

// ------------------------------------------------------------------ normalizing hub rows

const RIDESHARE =
  /uber|lyft|waymo|rideshare|bolt|grab|didi|careem|cabify|freenow|free now|ola cabs/i;
const TAXI = /taxi|\bcab\b/i;
const BUS = /\bbus\b|\bcoach\b|megabus|flixbus|national express|greyhound/i;

type Obj = Record<string, unknown>;

function parseExtra(v: unknown): Obj {
  if (v && typeof v === 'object') return v as Obj;
  if (typeof v !== 'string' || !v) return {};
  try {
    const o = JSON.parse(v);
    return o && typeof o === 'object' ? (o as Obj) : {};
  } catch {
    return {};
  }
}

/** Same rules as Trace's categoryOf: who was driving, and paid rides. */
export function categoryOf(d: Obj): string {
  const type = String(d.type ?? 'other');
  switch (type) {
    case 'walk':
    case 'run':
    case 'hike':
    case 'ride':
    case 'train':
    case 'transit':
    case 'boat':
    case 'ski':
      return type;
    case 'drive': {
      const x = parseExtra(d.extra);
      if (x.taxi === true) return 'taxi';
      if (x.bus === true) return 'bus';
      const named = `${d.sport ?? ''} ${d.source ?? ''}`;
      const taxi = x.taxi !== false && TAXI.test(named) && !RIDESHARE.test(named);
      if (x.rideshare === true) return taxi ? 'taxi' : 'rideshare';
      if (d.role !== 'passenger') return 'drive';
      if (x.rideshare === false) return 'passenger';
      return taxi
        ? 'taxi'
        : RIDESHARE.test(named)
          ? 'rideshare'
          : x.bus !== false && BUS.test(named)
            ? 'bus'
            : 'passenger';
    }
    default:
      return 'other';
  }
}

const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
const num = (v: unknown) =>
  typeof v === 'number' && Number.isFinite(v)
    ? v
    : typeof v === 'string' && v !== '' && Number.isFinite(Number(v))
      ? Number(v)
      : undefined;

export function normalizeTrack(id: string, d: Obj): Movement | null {
  const start = Date.parse(String(d.start_time ?? ''));
  if (!Number.isFinite(start)) return null;
  let end = Date.parse(String(d.end_time ?? ''));
  if (!Number.isFinite(end)) end = start + (num(d.duration_s) ?? 0) * 1000;
  const x = parseExtra(d.extra);
  const trace = (x.trace && typeof x.trace === 'object' ? x.trace : {}) as Obj;
  return {
    id,
    category: categoryOf(d),
    start,
    end,
    distanceM: num(d.distance_m),
    name: str(d.name),
    fromName: str(x.from),
    toName: str(x.to),
    tz: str(trace.tz),
    source: str(d.source),
  };
}

export function applyRows(c: WorldCache, rows: { t: string; id: string; x: number; d: Obj }[]) {
  for (const r of rows) {
    const del = r.x === 1 || r.d?.deleted === 1 || r.d?.deleted === true;
    const d = r.d ?? {};
    switch (r.t) {
      case 'tracks': {
        const m = del ? null : normalizeTrack(r.id, d);
        if (m) c.tracks[r.id] = m;
        else delete c.tracks[r.id];
        break;
      }
      case 'flights': {
        const date = str(d.date);
        if (del || !date) delete c.flights[r.id];
        else
          c.flights[r.id] = {
            id: r.id,
            date: date.slice(0, 10),
            from: str(d.dep_code) ?? '?',
            to: str(d.arr_code) ?? '?',
            fromCity: str(d.dep_city),
            toCity: str(d.arr_city),
            flightNo: str(d.flight_no),
            km: num(d.distance_km),
            source: str(d.source),
          };
        break;
      }
      case 'stays': {
        const checkIn = str(d.check_in);
        if (del || !checkIn) delete c.stays[r.id];
        else
          c.stays[r.id] = {
            id: r.id,
            hotel: str(d.hotel_name) ?? 'a hotel',
            city: str(d.city),
            checkIn: checkIn.slice(0, 10),
            checkOut: (str(d.check_out) ?? checkIn).slice(0, 10),
          };
        break;
      }
      case 'places': {
        const name = str(d.name);
        if (del || !name) delete c.places[r.id];
        else
          c.places[r.id] = {
            id: r.id,
            name,
            kind: str(d.kind),
            category: str(d.kind),
            country: str(d.country),
            firstVisit: str(d.first_visit)?.slice(0, 10),
            date: str(d.first_visit)?.slice(0, 10),
            wantToGo: d.want_to_go === 1 || d.want_to_go === true,
          };
        break;
      }
      case 'sightings': {
        const day = str(d.day);
        if (del || !day) delete c.sightings[r.id];
        else
          c.sightings[r.id] = {
            id: r.id,
            day: day.slice(0, 10),
            city: str(d.city),
            country: str(d.country),
          };
        break;
      }
      default:
        break;
    }
  }
}

let pulling: Promise<number> | null = null;

/** Fetch everything new from the hub into the local cache. Returns rows applied. */
export function pullWorld(): Promise<number> {
  if (pulling) return pulling;
  pulling = (async () => {
    const conn = getWorldConn();
    if (!conn) return 0;
    let c = (await loadWorldCache()) ?? emptyCache(conn.origin);
    if (c.origin !== conn.origin) c = emptyCache(conn.origin);
    let applied = 0;
    for (let page = 0; page < 80; page++) {
      let res: Response;
      try {
        res = await fetch(`${conn.origin}/sync/pull?since=${c.next}&limit=1000`, {
          headers: { Authorization: `Bearer ${conn.token}` },
        });
      } catch {
        throw new Error("Can't reach The World App right now");
      }
      if (res.status === 401)
        throw new Error(`${APP_NAME} was removed from Trace. Connect it again with a new code.`);
      if (!res.ok) throw new Error(await readError(res));
      const j = (await res.json()) as {
        rows: { t: string; id: string; x: number; d: Obj }[];
        next: number;
        more: boolean;
      };
      applyRows(c, j.rows ?? []);
      applied += j.rows?.length ?? 0;
      c.next = j.next ?? c.next;
      if (!j.more) break;
    }
    c.syncedAt = Date.now();
    cache = c;
    await saveCache(c);
    emit();
    return applied;
  })().finally(() => {
    pulling = null;
  });
  return pulling;
}

// ------------------------------------------------------------------ a day's movement

/** Local calendar day of a moment, in the trip's own time zone when known. */
export function localDay(ms: number, tz?: string): string {
  if (tz) {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(ms);
    } catch {
      // unknown zone: fall back to the display zone
    }
  }
  return dayInZone(ms);
}

export interface DayMovement {
  tracks: Movement[];
  flights: Flight[];
  stays: { stay: Stay; status: 'in' | 'staying' | 'out' }[];
  newPlaces: Place[];
  cities: string[];
}

export function movementOn(c: WorldCache | null, date: string): DayMovement {
  if (!c) return { tracks: [], flights: [], stays: [], newPlaces: [], cities: [] };
  const tracks = Object.values(c.tracks)
    .filter((t) => localDay(t.start, t.tz) === date)
    .sort((a, b) => a.start - b.start);
  const flights = Object.values(c.flights).filter((f) => f.date === date);
  const stays = Object.values(c.stays)
    .filter((s) => s.checkIn <= date && date <= s.checkOut)
    .map((stay) => ({
      stay,
      status: (stay.checkIn === date ? 'in' : stay.checkOut === date ? 'out' : 'staying') as
        | 'in'
        | 'staying'
        | 'out',
    }));
  const newPlaces = Object.values(c.places).filter((p) => p.firstVisit === date && !p.wantToGo);
  const cities = [
    ...new Set(
      Object.values(c.sightings)
        .filter((s) => s.day === date && s.city)
        .map((s) => (s.country ? `${s.city}, ${s.country}` : s.city!)),
    ),
  ];
  return { tracks, flights, stays, newPlaces, cities };
}

const CATS: Record<string, { verb: string; noun: string }> = {
  walk: { verb: 'Walked', noun: 'walk' },
  run: { verb: 'Ran', noun: 'run' },
  hike: { verb: 'Hiked', noun: 'hike' },
  ride: { verb: 'Rode', noun: 'ride' },
  drive: { verb: 'Drove', noun: 'drive' },
  rideshare: { verb: 'Rideshare', noun: 'ride' },
  taxi: { verb: 'Taxi', noun: 'ride' },
  bus: { verb: 'Bus', noun: 'ride' },
  passenger: { verb: 'Passenger', noun: 'ride' },
  train: { verb: 'Train', noun: 'trip' },
  boat: { verb: 'Boat', noun: 'trip' },
  transit: { verb: 'Transit', noun: 'trip' },
  ski: { verb: 'Skied', noun: 'run' },
  other: { verb: 'Moved', noun: 'trip' },
};
const ORDER = Object.keys(CATS);

export function miles(m: number): string {
  const mi = m / 1609.344;
  return mi >= 10 ? `${Math.round(mi)} mi` : `${mi.toFixed(1).replace(/\.0$/, '')} mi`;
}

/** "Walked 3.2 mi (2 walks) · Drove 14 mi · Uber 6 mi" */
export function movementLine(tracks: Movement[]): string {
  const by = new Map<string, Movement[]>();
  for (const t of tracks) {
    const arr = by.get(t.category) ?? [];
    arr.push(t);
    by.set(t.category, arr);
  }
  const parts: string[] = [];
  for (const cat of [...by.keys()].sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b))) {
    const list = by.get(cat)!;
    const meta = CATS[cat] ?? CATS.other!;
    const dist = list.reduce((s, t) => s + (t.distanceM ?? 0), 0);
    let verb = meta.verb;
    if (cat === 'rideshare') {
      const srcs = new Set(list.map((t) => (t.source ?? '').toLowerCase()).filter(Boolean));
      if (srcs.size === 1) {
        const only = [...srcs][0]!;
        if (/uber|lyft|waymo|bolt|grab/.test(only))
          verb = only.charAt(0).toUpperCase() + only.slice(1);
      }
    }
    const count = list.length > 1 ? ` (${list.length} ${meta.noun}s)` : '';
    parts.push(
      dist > 0
        ? `${verb} ${miles(dist)}${count}`
        : `${verb}${count || ` (${list.length} ${meta.noun})`}`,
    );
  }
  return parts.join(' · ');
}
